import { isFunction } from "lodash";
import { ReactNode } from "react";

import ErrorCard from "./ErrorCard";
import Loading, { LoadingProps } from "./Loading";

type BaseProps = {
  loading: boolean | boolean[];
  loadingProps?: LoadingProps;
  errors: any | any[];
};

export function AsyncWrapper(
  props: BaseProps & { children: ReactNode }
): ReactNode;

export function AsyncWrapper<T>(
  props: BaseProps & { data: T | undefined; children: (data: T) => ReactNode }
): ReactNode;

// Given a loading state (or states), loading props (optional), error (or errors), and data (optional),
// this component will render a loading spinner, an error card, or the children component with the data.
// If the children is a function, it will be called with the data.
export function AsyncWrapper<T>({
  loading,
  loadingProps = {},
  errors,
  data,
  children,
}: BaseProps & {
  data?: T;
  children: ReactNode | ((data: T) => ReactNode);
}): ReactNode {
  const isLoading = Array.isArray(loading) ? loading.some(Boolean) : loading;
  if (isLoading) {
    return <Loading {...loadingProps} />;
  }

  const hasErrors = Array.isArray(errors) ? errors.some(Boolean) : errors;
  if (hasErrors) {
    return (
      <ErrorCard
        errors={Array.isArray(errors) ? errors.filter(Boolean) : [errors]}
      />
    );
  }

  if (isFunction(children)) {
    return data ? children(data) : null;
  }

  return children;
}
